import { useForm, validators } from "@hooks";
import { Button, ShowPassword, TextField } from "@common";
import { ReactNode } from "react";

interface CredentialsValues {
    email: string;
    password: string;
    confirm: string;
}

interface CredentialsProps {
    submit: (values: CredentialsValues) => void | Promise<void>;
    initial: {
        email: string;
    };
    children: ReactNode;
    onCancel?: () => void;
}

const Credentials = ({ submit, initial, children, onCancel }: CredentialsProps) => {

    const initialState: CredentialsValues = { password: ``, confirm: ``, ...initial };

    const validations = {
        email: (val: string) => validators.isValidEmail(val) || { email: `Please use a valid email address.` },
        password: (val: string) => validators.validatePassword(val),
        confirm: (val: string, values: CredentialsValues) => compare(val, values.password) || { confirm: `Passwords do not match` }
    };

    const compare = (val: string, match: string) => val === match;

    const { values, errors, changeHandler, submitHandler } = useForm(initialState, {}, validations, submit);

    const isFormSubmittable = values.email && values.email.trim().length > 0
        && values.password && values.password.trim().length > 0
        && values.confirm && values.confirm.trim().length > 0;

    return (
        <div className="flex flex-col w-full">
            <div className="border-b border-neutral-100 pb-6 mb-6 md:mb-8">
                {children}
            </div>
            <form method="POST" className="w-full flex flex-col gap-6" onSubmit={submitHandler} >
                <TextField
                    type="email"
                    name="email"
                    placeholder="Email*"
                    value={values.email}
                    readOnly={true}
                    onChange={changeHandler}
                    error={errors.email}
                />
                <div>
                    <ShowPassword
                        maxLength={100}
                        name="password"
                        placeholder="Password*"
                        value={values.password}
                        onChange={changeHandler}
                        helperText="Password must be at least 9 characters and be a mix of upper and lowercase letters, numbers, and at least one special character"
                        error={errors.password}
                    />
                </div>
                <ShowPassword
                    maxLength={100}
                    name="confirm"
                    placeholder="Confirm Password*"
                    value={values.confirm}
                    onChange={changeHandler}
                    error={errors.confirm}
                />
                <div className="flex flex-col-reverse md:flex-row gap-4 items-center w-full mt-2">
                    {onCancel && (
                        <Button
                            variant="secondary"
                            size="lg"
                            className="w-full md:flex-1"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full md:flex-1"
                        disabled={!isFormSubmittable}
                    >
                        Create Account
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default Credentials;
